/**
 * InputSearch Component - Controller
 * ==================================
 */

import type { IScope, ISCEService, INgModelController, ITranscludeFunction } from 'angular';
import { SIZES, BASE_CLASSES, ICONS, DEFAULTS, InputSearchSize } from './constants';

export interface InputSearchBindings {
  placeholder?: string;
  showFilter?: boolean;
  disabled?: boolean;
  size?: InputSearchSize;
  ariaLabel?: string;
  onSearch?: (args: { value: string }) => void;
  onClear?: () => void;
  onFilter?: () => void;
}

export class InputSearchController implements InputSearchBindings {
  static $inject = ['$scope', '$sce', '$transclude'];

  private $scope: IScope | null;
  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  ngModelCtrl?: INgModelController | null;

  placeholder?: string;
  showFilter?: boolean;
  disabled?: boolean;
  size?: InputSearchSize;
  ariaLabel?: string;
  onSearch?: (args: { value: string }) => void;
  onClear?: () => void;
  onFilter?: () => void;

  value = '';
  trustedSearch: unknown = null;
  trustedClear: unknown = null;
  trustedFilter: unknown = null;

  // Capacidad nueva: id del input + label/help vía slots (sin bindings nuevos,
  // solo visibles si el consumidor transcluye <ec-label>/<ec-help>).
  resolvedId = '';
  helpId = '';
  hasLabelSlot = false;
  hasHelpSlot = false;

  constructor($scope: IScope, $sce: ISCEService, $transclude: ITranscludeFunction) {
    this.$scope = $scope;
    this.$sce = $sce;
    this.$transclude = $transclude;
  }

  $onInit(): void {
    this.size = this.size || DEFAULTS.size;
    this.ariaLabel = this.ariaLabel || 'Buscar';
    const uid = this.$scope ? String(this.$scope.$id) : Math.random().toString(36).slice(2);
    this.resolvedId = 'ec-search-' + uid;
    this.helpId = 'ec-search-help-' + uid;
    if (this.$transclude) {
      this.hasLabelSlot = this.$transclude.isSlotFilled('labelSlot');
      this.hasHelpSlot = this.$transclude.isSlotFilled('helpSlot');
    }
    if (this.$sce) {
      this.trustedSearch = this.$sce.trustAsHtml(ICONS.search);
      this.trustedClear = this.$sce.trustAsHtml(ICONS.clear);
      this.trustedFilter = this.$sce.trustAsHtml(ICONS.filter);
    }
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$render = () => {
        const v = this.ngModelCtrl ? this.ngModelCtrl.$viewValue : null;
        this.value = v != null ? String(v) : '';
      };
    }
  }

  $onDestroy(): void {
    this.$scope = null;
    this.$sce = null;
    this.$transclude = null;
    this.ngModelCtrl = null;
  }

  handleInput(): void {
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$setViewValue(this.value);
    }
  }

  handleKeydown($event: KeyboardEvent): void {
    if ($event.key === 'Enter' && typeof this.onSearch === 'function') {
      this.onSearch({ value: this.value });
    }
  }

  clear(): void {
    this.value = '';
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$setViewValue('');
    }
    if (typeof this.onClear === 'function') {
      this.onClear();
    }
  }

  filter(): void {
    if (this.disabled) return;
    if (typeof this.onFilter === 'function') {
      this.onFilter();
    }
  }

  getWrapperClasses(): string[] { return [BASE_CLASSES.wrapper]; }

  getBoxClasses(): string[] {
    const classes = [BASE_CLASSES.box, SIZES[this.size || DEFAULTS.size].box, SIZES[this.size || DEFAULTS.size].input];
    if (this.disabled) classes.push(BASE_CLASSES.boxDisabled);
    return classes;
  }

  getInputClasses(): string[] { return [BASE_CLASSES.input]; }
  // Ícono search/filter: 24px fijo (§3). El clear usa su propio getter (16px).
  getIconClasses(): string[] { return [SIZES[this.size || DEFAULTS.size].icon, BASE_CLASSES.searchIcon]; }
  getClearIconClasses(): string[] { return [BASE_CLASSES.clearIcon]; }
  getClearClasses(): string[] { return [BASE_CLASSES.clearBtn]; }
  getFilterClasses(): string[] { return [BASE_CLASSES.filterBtn]; }

  getFieldClasses(): string[] { return [BASE_CLASSES.field]; }
  getLabelClasses(): string[] { return [BASE_CLASSES.label]; }
  getHelpClasses(): string[] { return [BASE_CLASSES.help]; }

  get hasValue(): boolean {
    return !!this.value && this.disabled !== true;
  }
}

export default InputSearchController;
